/**
 * SONE — access control.
 *
 * Authorisation resolves to *claims*, not to an identity. A logged-in member,
 * a named guest and an anonymous visitor holding a share link all arrive at
 * the sync server as a set of capabilities over a page subtree. That is the
 * only way link-based editing works without bolting a parallel permission
 * system onto the side later (ADR-0006).
 */
/** Ordered from least to most privileged; comparison is by index. */
export const ROLE_ORDER = ['viewer', 'commenter', 'editor', 'admin'];
export const atLeast = (have, need) => ROLE_ORDER.indexOf(have) >= ROLE_ORDER.indexOf(need);
/**
 * The rights a role can carry (ADR-0087).
 *
 * A closed list, and short on purpose: it holds exactly the rights that gate
 * something on the server today. `scripts/check-rights-enforced.mjs` fails the
 * build when an entry here is not consulted by any check, because a right
 * nobody checks is a lie — and a lie in a permission screen is worse than a
 * missing feature, since somebody turns the switch off and believes something.
 *
 * So the way to add one is: write the check first, then the entry. Rights for
 * things nobody guards yet — creating pages, emptying the trash, exporting —
 * are deliberately absent. Exporting in particular is worth naming: today any
 * member may export a whole workspace, and tightening that is a change to what
 * SONE does rather than a rename of it, so it does not belong in the change
 * that only moves checks around.
 */
export const RIGHTS = [
    /** Add and remove members, change what role they hold, manage invitations. */
    'people.manage',
    /** Create groups and change who is in them. */
    'groups.manage',
    /** The workspace's name, mark, typography and appearance. */
    'workspace.settings',
    /**
     * Define roles: what each one gives, and who holds it.
     *
     * The most consequential of the four, because it is the one that can hand
     * out the other three. Held by owner and admin, which is who could change
     * what a role means before there was anything to change — until roles were
     * rows, the four were four words in the code.
     */
    'roles.manage',
];
//# sourceMappingURL=access.js.map